import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Link,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { AvailabilityBounds, AvailabilityFilters, FacetBucket } from "../types";
import { FilterPill, FilterBarWrap } from "../Availability.style";
import { formatBedrooms } from "../format";

interface Props {
  filters: AvailabilityFilters;
  bounds: AvailabilityBounds;
  facets: {
    bedrooms: FacetBucket[];
    unitType: FacetBucket[];
    floorLevel: FacetBucket[];
  };
  onChange: <K extends keyof AvailabilityFilters>(
    key: K,
    value: AvailabilityFilters[K]
  ) => void;
  onClear: () => void;
  onChangeMoveIn: () => void;
}

const toggle = (arr: number[], v: number) =>
  arr.includes(v)
    ? arr.filter((n) => n !== v)
    : [...arr, v].sort((a, b) => a - b);

const toggleString = (arr: string[], v: string) =>
  arr.includes(v) ? arr.filter((s) => s !== v) : [...arr, v].sort();

const useAnchor = () => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return {
    anchor,
    open: (e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget),
    close: () => setAnchor(null),
  };
};

export const FilterBar: React.FC<Props> = ({
  filters,
  bounds,
  facets,
  onChange,
  onClear,
  onChangeMoveIn,
}) => {
  const beds = useAnchor();
  const floorplan = useAnchor();
  const rent = useAnchor();
  const more = useAnchor();
  const unit = useAnchor();

  const bedsLabel =
    filters.bedrooms.length === 0
      ? "Beds"
      : filters.bedrooms.map((n) => formatBedrooms(n)).join(", ");

  const rentLabel =
    filters.rentMin == null && filters.rentMax == null
      ? "Base Rent"
      : `$${filters.rentMin ?? bounds.rent.min} - $${
          filters.rentMax ?? bounds.rent.max
        }`;

  const floorplanLabel =
    filters.floorplans.length === 0
      ? "Floor Plan"
      : filters.floorplans.length === 1
      ? `Floor Plan: ${filters.floorplans[0]}`
      : `Floor Plan: ${filters.floorplans.length} selected`;

  const unitLabel = filters.unitNumber
    ? `Unit #: ${filters.unitNumber}`
    : "Unit #";

  const moreActive =
    filters.floorLevel.length > 0 ||
    filters.petsPermitted === true ||
    filters.sqftMin != null ||
    filters.sqftMax != null;

  const moreLabel = moreActive ? "More Filters ·" : "More Filters";
  const moreCount =
    (filters.floorLevel.length > 0 ? 1 : 0) +
    (filters.petsPermitted === true ? 1 : 0) +
    (filters.sqftMin != null || filters.sqftMax != null ? 1 : 0);

  const anyActive =
    filters.bedrooms.length > 0 ||
    filters.floorplans.length > 0 ||
    filters.unitNumber != null ||
    filters.rentMin != null ||
    filters.rentMax != null ||
    moreActive;

  return (
    <FilterBarWrap>
      <FilterPill
        onClick={beds.open}
        active={filters.bedrooms.length > 0 ? 1 : 0}
      >
        {bedsLabel}
        <ArrowDropDownIcon fontSize="small" />
      </FilterPill>

      <FilterPill
        onClick={floorplan.open}
        active={filters.floorplans.length > 0 ? 1 : 0}
      >
        {floorplanLabel}
        <ArrowDropDownIcon fontSize="small" />
      </FilterPill>

      <FilterPill
        onClick={unit.open}
        active={filters.unitNumber != null ? 1 : 0}
      >
        {unitLabel}
        <ArrowDropDownIcon fontSize="small" />
      </FilterPill>

      <FilterPill onClick={onChangeMoveIn} active={0}>
        Move-in: {filters.moveInDate}
      </FilterPill>

      <FilterPill
        onClick={rent.open}
        active={filters.rentMin != null || filters.rentMax != null ? 1 : 0}
      >
        {rentLabel}
        <ArrowDropDownIcon fontSize="small" />
      </FilterPill>

      <FilterPill onClick={more.open} active={moreActive ? 1 : 0}>
        {moreLabel}
        {moreCount > 0 && ` ${moreCount}`}
        <ArrowDropDownIcon fontSize="small" />
      </FilterPill>

      {anyActive && (
        <Link
          component="button"
          type="button"
          onClick={onClear}
          sx={{ fontSize: 13, ml: 1 }}
        >
          Clear All
        </Link>
      )}

      {/* Beds popover */}
      <Popover
        open={Boolean(beds.anchor)}
        anchorEl={beds.anchor}
        onClose={beds.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Bedrooms
          </Typography>
          {facets.bedrooms.length === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1 }}
            >
              No matches
            </Typography>
          )}
          {facets.bedrooms.map((b) => {
            const n = parseInt(b.key, 10);
            return (
              <FormControlLabel
                key={b.key}
                control={
                  <Checkbox
                    size="small"
                    checked={filters.bedrooms.includes(n)}
                    onChange={() =>
                      onChange("bedrooms", toggle(filters.bedrooms, n))
                    }
                    disabled={
                      b.doc_count === 0 && !filters.bedrooms.includes(n)
                    }
                  />
                }
                label={`${formatBedrooms(n)} (${b.doc_count})`}
                sx={{
                  display: "flex",
                  m: 0,
                  ".MuiFormControlLabel-label": { fontSize: 13 },
                }}
              />
            );
          })}
        </Box>
      </Popover>

      {/* Floor plan popover */}
      <Popover
        open={Boolean(floorplan.anchor)}
        anchorEl={floorplan.anchor}
        onClose={floorplan.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Floor Plan
          </Typography>
          {facets.unitType.length === 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1 }}
            >
              No matches
            </Typography>
          )}
          {facets.unitType.map((b) => {
            const checked = filters.floorplans.includes(b.key);
            return (
              <FormControlLabel
                key={b.key}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => {
                      onChange(
                        "floorplans",
                        toggleString(filters.floorplans, b.key)
                      );
                    }}
                    disabled={b.doc_count === 0 && !checked}
                  />
                }
                label={`${b.key} (${b.doc_count})`}
                sx={{
                  display: "flex",
                  m: 0,
                  ".MuiFormControlLabel-label": { fontSize: 13 },
                }}
              />
            );
          })}
        </Box>
      </Popover>

      {/* Unit number popover */}
      <Popover
        open={Boolean(unit.anchor)}
        anchorEl={unit.anchor}
        onClose={unit.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            p: 2,
            minWidth: 220,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Unit Number
          </Typography>
          <TextField
            size="small"
            placeholder="e.g. 1202"
            value={filters.unitNumber ?? ""}
            onChange={(e) => onChange("unitNumber", e.target.value || null)}
            autoFocus
          />
          <Typography variant="caption" color="text.secondary">
            Exact match
          </Typography>
        </Box>
      </Popover>

      {/* Rent popover */}
      <Popover
        open={Boolean(rent.anchor)}
        anchorEl={rent.anchor}
        onClose={rent.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            p: 2,
            minWidth: 240,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Base Rent
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Min"
              size="small"
              type="number"
              value={filters.rentMin ?? ""}
              onChange={(e) =>
                onChange(
                  "rentMin",
                  e.target.value ? Number(e.target.value) : null
                )
              }
              inputProps={{ min: bounds.rent.min, max: bounds.rent.max }}
            />
            <TextField
              label="Max"
              size="small"
              type="number"
              value={filters.rentMax ?? ""}
              onChange={(e) =>
                onChange(
                  "rentMax",
                  e.target.value ? Number(e.target.value) : null
                )
              }
              inputProps={{ min: bounds.rent.min, max: bounds.rent.max }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Range: ${bounds.rent.min} – ${bounds.rent.max}
          </Typography>
        </Box>
      </Popover>

      {/* More filters popover */}
      <Popover
        open={Boolean(more.anchor)}
        anchorEl={more.anchor}
        onClose={more.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            p: 2,
            minWidth: 260,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {facets.floorLevel.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Floor
              </Typography>
              {facets.floorLevel.map((b) => {
                const n = parseInt(b.key, 10);
                return (
                  <FormControlLabel
                    key={b.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.floorLevel.includes(n)}
                        onChange={() =>
                          onChange("floorLevel", toggle(filters.floorLevel, n))
                        }
                        disabled={
                          b.doc_count === 0 && !filters.floorLevel.includes(n)
                        }
                      />
                    }
                    label={`${b.key} (${b.doc_count})`}
                    sx={{
                      display: "flex",
                      m: 0,
                      ".MuiFormControlLabel-label": { fontSize: 13 },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {facets.floorLevel.length > 0 && <Divider />}

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Square Footage
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <TextField
                label="Min"
                size="small"
                type="number"
                value={filters.sqftMin ?? ""}
                onChange={(e) =>
                  onChange(
                    "sqftMin",
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              />
              <TextField
                label="Max"
                size="small"
                type="number"
                value={filters.sqftMax ?? ""}
                onChange={(e) =>
                  onChange(
                    "sqftMax",
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Pets
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filters.petsPermitted === true}
                  onChange={(e) =>
                    onChange("petsPermitted", e.target.checked ? true : null)
                  }
                />
              }
              label="Pets permitted"
              sx={{
                display: "flex",
                m: 0,
                ".MuiFormControlLabel-label": { fontSize: 13 },
              }}
            />
          </Box>

          <Button
            size="small"
            onClick={more.close}
            variant="contained"
            sx={{ mt: 1 }}
          >
            Done
          </Button>
        </Box>
      </Popover>
    </FilterBarWrap>
  );
};
